import {RefObject, useEffect, useLayoutEffect, useMemo, useState} from 'react'
import {InvoicePrintModel, PrintOrientation, PrintOrientationChoice} from './_models'
import {PrintColumn} from './printColumns'
import {layoutCandidates, PAGE_SAFETY_MM, PX_PER_MM, sheetInnerSizePx} from './printLayout'
import {rowsTotalsBySheet, RowsTotals} from './printModel'
import {FlowSection, PagePart, paginate, sheetsWithRows} from './printPagination'

// Lays the invoice out on A4 sheets in the browser that prints it:
// 1. fit: the rows' table is laid out by the browser at the largest font size (and, in the
//    automatic choice, portrait first) that keeps it within the sheet's width, and its column
//    widths are frozen, so every sheet's table has the same columns;
// 2. measure: the rows with the frozen widths (exactly as the sheets print them) and every other
//    part are measured;
// 3. paginate: parts are placed on sheets (printPagination), with the page and running totals at
//    the foot of each sheet when the rows take more than one;
// 4. verify: the rendered sheets are checked for overflow and laid out again with a larger reserve
//    if one overflows, so no row is ever cut off on paper; a page or running total wider than its
//    column has the columns fitted again around the totals the sheets print.

type RowsMeasure = {head: number; foot: number; rows: number[]}

type Measured = {
  headFull: number
  headCompact: number
  foot: number
  blocks: Record<string, number>
  items: RowsMeasure | null
  payments: RowsMeasure | null
  notes: RowsMeasure | null
}

export type PrintLayout = {
  ready: boolean
  orientation: PrintOrientation
  fontPt: number
  tight: boolean
  /** The frozen column widths the measuring copy prints with; null while the table is being fitted. */
  measureWidths: number[] | null
  widths: number[]
  pages: PagePart[][]
  rowsTotals: Map<number, RowsTotals> | null
  /** Totals the measuring copy lays out in collapsed rows, so the columns hold them. */
  probeTotals: TotalsRecord[]
}

type TotalsRecord = Record<string, string | null>

const VERIFY_STEP_PX = 4 * PX_PER_MM
const VERIFY_ATTEMPTS = 4
/** Fittings around the totals actually printed before falling back to the widest digit. */
const PROBE_ROUNDS = 2

const heightOf = (element: Element | null | undefined) =>
  element ? element.getBoundingClientRect().height : 0

const sumHeights = (elements: Element[]) =>
  elements.reduce((sum, element) => sum + heightOf(element), 0)

const measureRows = (
  frame: Element | null,
  headSelectors: string[],
  rowSelector: string,
  footSelector?: string
): RowsMeasure | null => {
  if (!frame) return null
  const rows = Array.from(frame.querySelectorAll(rowSelector))
  const head = sumHeights(
    headSelectors.map((selector) => frame.querySelector(selector)).filter(Boolean) as Element[]
  )
  const foot = footSelector ? heightOf(frame.querySelector(footSelector)) : 0
  const rowHeights = rows.map(heightOf)
  // Borders, gaps and the frame's spacing: everything that is not head, rows or foot travels with the head.
  const extra = Math.max(0, heightOf(frame) - head - foot - rowHeights.reduce((a, b) => a + b, 0))
  return {head: head + extra, foot, rows: rowHeights}
}

const measure = (root: HTMLElement): Measured => {
  const block = (key: string) => root.querySelector(`[data-measure-block="${key}"]`)
  const blocks: Record<string, number> = {}
  ;['parties', 'pattern', 'totals', 'notices', 'signatures', 'signatures-compact'].forEach(
    (key) => {
      const element = block(key)
      if (element) blocks[key] = heightOf(element)
    }
  )

  const items = measureRows(
    block('items'),
    ['[data-measure="items-caption"]', '[data-measure="items-head"]'],
    'tr[data-measure="items-row"]',
    '[data-measure="items-foot"]'
  )
  const payments = measureRows(
    block('payments'),
    ['[data-measure="payments-title"]', '[data-measure="payments-head"]'],
    'tr[data-measure="payments-row"]'
  )
  if (payments) {
    // The sums under the payments print after the last payment and move with it.
    const summary = heightOf(block('payments')?.querySelector('[data-measure="payments-summary"]'))
    if (payments.rows.length) payments.rows[payments.rows.length - 1] += summary
    payments.head = Math.max(0, payments.head - summary)
  }
  const notes = measureRows(
    block('notes'),
    ['[data-measure="notes-title"]'],
    '[data-measure="notes-line"]'
  )

  return {
    headFull: heightOf(block('head-full')),
    headCompact: heightOf(block('head-compact')),
    foot: heightOf(block('foot')),
    blocks,
    items,
    payments,
    notes,
  }
}

const plan = (measured: Measured, orientation: PrintOrientation, extraSafety: number) => {
  const inner = sheetInnerSizePx(orientation).height
  const reserve = PAGE_SAFETY_MM * PX_PER_MM + extraSafety
  const firstCapacity = inner - measured.headFull - measured.foot - reserve
  const nextCapacity = inner - measured.headCompact - measured.foot - reserve

  const sections = (itemsFoot: number): FlowSection[] => {
    const list: FlowSection[] = [
      {kind: 'block', key: 'parties', height: measured.blocks.parties || 0},
    ]
    if (measured.blocks.pattern !== undefined)
      list.push({kind: 'block', key: 'pattern', height: measured.blocks.pattern})
    if (measured.items && measured.items.rows.length) {
      list.push({
        kind: 'rows',
        key: 'items',
        headHeight: measured.items.head,
        footHeight: itemsFoot,
        rowHeights: measured.items.rows,
        keepLastRowWithNext: true,
      })
    }
    list.push({kind: 'block', key: 'totals', height: measured.blocks.totals || 0})
    if (measured.payments && measured.payments.rows.length) {
      list.push({
        kind: 'rows',
        key: 'payments',
        headHeight: measured.payments.head,
        footHeight: 0,
        rowHeights: measured.payments.rows,
      })
    }
    if (measured.notes && measured.notes.rows.length) {
      list.push({
        kind: 'rows',
        key: 'notes',
        headHeight: measured.notes.head,
        footHeight: 0,
        rowHeights: measured.notes.rows,
      })
    }
    if (measured.blocks.notices !== undefined)
      list.push({kind: 'block', key: 'notices', height: measured.blocks.notices})
    list.push({
      kind: 'block',
      key: 'signatures',
      height: measured.blocks.signatures || 0,
      compactHeight: measured.blocks['signatures-compact'],
    })
    return list
  }

  let result = paginate({firstCapacity, nextCapacity, sections: sections(0)})
  let multiPage = false
  if (measured.items && sheetsWithRows(result, 'items') > 1) {
    // Rows on more than one sheet: every one of those sheets ends with its page and running totals.
    multiPage = true
    result = paginate({firstCapacity, nextCapacity, sections: sections(measured.items.foot)})
  }
  return {pages: result.pages, multiPage}
}

const FONT_FACES = [
  '400 10pt "Vazirmatn FD"',
  '600 10pt "Vazirmatn FD"',
  '700 10pt "Vazirmatn FD"',
  '400 10pt Vazirmatn',
  '700 10pt Vazirmatn',
]

const fontApi = () =>
  typeof document !== 'undefined' ? (document as Document & {fonts?: FontFaceSet}).fonts : undefined

/** Resolves once the print faces are loaded (or after a few seconds, so a font failure never blocks printing). */
export const loadPrintFonts = (): Promise<void> => {
  const fonts = fontApi()
  if (!fonts || typeof fonts.load !== 'function') return Promise.resolve()
  const loaded = Promise.all(FONT_FACES.map((face) => fonts.load(face).catch(() => []))).then(
    () => fonts.ready
  )
  return Promise.race([
    loaded.then(() => undefined),
    new Promise<void>((resolve) => setTimeout(resolve, 4000)),
  ])
}

export const usePrintLayout = ({
  invoice,
  columns,
  noteLines,
  choice,
  showSignature,
  measureRef,
  sheetsRef,
}: {
  invoice: InvoicePrintModel | undefined
  columns: PrintColumn[]
  noteLines: string[]
  choice: PrintOrientationChoice
  showSignature: boolean
  measureRef: RefObject<HTMLDivElement>
  sheetsRef: RefObject<HTMLDivElement>
}): PrintLayout => {
  const candidates = useMemo(() => layoutCandidates(choice), [choice])
  // Without the font loading API (old browsers, tests) the page is measured as it stands.
  const [fontsReady, setFontsReady] = useState(() => typeof fontApi()?.load !== 'function')

  // The layout belongs to its inputs: when any of them changes, the state below starts over in
  // the same render (no effect resets it afterwards and races the measuring).
  const inputs = useMemo(
    () => ({invoice, candidates, columns, noteLines, showSignature}),
    [invoice, candidates, columns, noteLines, showSignature]
  )
  const [stored, setStored] = useState<LayoutState>(() => initialState(inputs))
  const state = stored.inputs === inputs ? stored : initialState(inputs)
  const update = (patch: Partial<LayoutState>) =>
    setStored((previous) => ({
      ...(previous.inputs === inputs ? previous : initialState(inputs)),
      ...patch,
    }))

  useEffect(() => {
    if (fontsReady) return
    let active = true
    loadPrintFonts().then(() => active && setFontsReady(true))
    return () => {
      active = false
    }
    // Fonts are loaded once per visit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const candidate = candidates[Math.min(state.attempt, candidates.length - 1)]
  const tight = state.attempt >= candidates.length

  // 1. Fit: the rows' table, laid out by the browser, must fit the sheet's body. Its column widths
  //    are then frozen.
  // 2. Measure: the same table with the frozen widths (what every sheet prints) and every other
  //    part are measured and paginated.
  useLayoutEffect(() => {
    if (!fontsReady || !invoice) return
    const root = measureRef.current
    if (!root) return
    if (!state.widths) {
      const body = root.querySelector('.ip-sheet__body--measure') as HTMLElement | null
      const table = root.querySelector('[data-measure-block="items"] table') as HTMLElement | null
      if (
        body &&
        table &&
        state.attempt < candidates.length &&
        table.getBoundingClientRect().width > body.clientWidth + 1
      ) {
        update({attempt: state.attempt + 1})
        return
      }
      update({widths: columnWidths(root)})
      return
    }
    const measured = measure(root)
    update({
      verified: false,
      result: {...plan(measured, candidate.orientation, state.extraSafety), widths: state.widths},
    })
    // update is recreated each render; the inputs it closes over are listed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    fontsReady,
    inputs,
    state.attempt,
    state.widths,
    state.extraSafety,
    candidate.orientation,
    measureRef,
  ])

  const result = state.result
  const rowsTotals = useMemo(
    () =>
      invoice && result?.multiPage ? rowsTotalsBySheet(invoice.items, columns, result.pages) : null,
    [invoice, result, columns]
  )

  // Verify the sheets as rendered.
  useLayoutEffect(() => {
    if (!state.result || state.verified) return
    const sheets = sheetsRef.current
    if (!sheets) return
    const overflowing = Array.from(sheets.querySelectorAll('.ip-sheet__body')).some(
      (body) => (body as HTMLElement).scrollHeight > (body as HTMLElement).clientHeight + 1
    )
    if (overflowing && state.extraSafety < VERIFY_STEP_PX * VERIFY_ATTEMPTS) {
      update({extraSafety: state.extraSafety + VERIFY_STEP_PX})
      return
    }
    // Persian digits differ in width (۳ is twice ۱), so a sheet's total can be wider than the
    // invoice's total the columns were fitted around. The columns are then fitted again around every
    // total these sheets print and, if the sheets still move, around totals of the widest digit.
    const wideCell = rowsTotals && !tight ? wideTotalCell(sheets) : null
    if (rowsTotals && wideCell && state.probeRound <= PROBE_ROUNDS) {
      const seen = [
        ...state.probe,
        ...Array.from(rowsTotals.values()).flatMap((totals) => [totals.page, totals.running]),
      ]
      update({
        probe:
          state.probeRound < PROBE_ROUNDS
            ? seen
            : [...seen, widestTotals(seen, widestDigit(wideCell))],
        probeRound: state.probeRound + 1,
        widths: null,
        result: null,
        extraSafety: 0,
      })
      return
    }
    update({verified: true})
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.result, state.verified, state.extraSafety, rowsTotals, tight, sheetsRef])

  return {
    ready: !!result && state.verified,
    orientation: candidate.orientation,
    fontPt: candidate.fontPt,
    tight,
    measureWidths: state.widths,
    widths: result?.widths || [],
    pages: result?.pages || [],
    rowsTotals,
    probeTotals: state.probe,
  }
}

type LayoutState = {
  inputs: object
  /** Index into the candidates; past the end means the tightest fallback. */
  attempt: number
  /** The rows' column widths once the table fits; the measuring copy then prints with them. */
  widths: number[] | null
  /** Extra reserve per sheet after a rendered sheet overflowed. */
  extraSafety: number
  verified: boolean
  result: {pages: PagePart[][]; multiPage: boolean; widths: number[]} | null
  /** Page and running totals the columns are fitted around besides the invoice's total. */
  probe: TotalsRecord[]
  probeRound: number
}

const initialState = (inputs: object): LayoutState => ({
  inputs,
  attempt: 0,
  widths: null,
  extraSafety: 0,
  verified: false,
  result: null,
  probe: [],
  probeRound: 0,
})

/** A page or running total that does not fit its cell on a rendered sheet. */
const wideTotalCell = (sheets: HTMLElement): HTMLElement | null =>
  (Array.from(sheets.querySelectorAll('.ip-table__total td')) as HTMLElement[]).find(
    (cell) => cell.scrollWidth > cell.clientWidth + 1
  ) || null

/** The digit that prints widest in the cell's font. */
const widestDigit = (cell: HTMLElement): string => {
  const context = document.createElement('canvas').getContext('2d')
  if (!context) return '3'
  const style = getComputedStyle(cell)
  context.font = `${style.fontWeight} ${style.fontSize} ${style.fontFamily}`
  return '0123456789'
    .split('')
    .reduce(
      (widest, digit) =>
        context.measureText(digit).width > context.measureText(widest).width ? digit : widest,
      '0'
    )
}

/** Each column's longest total written with the widest digit: no total of as many digits prints wider. */
export const widestTotals = (records: TotalsRecord[], digit: string): TotalsRecord => {
  const digitCount = (value: string) => value.replace(/\D/g, '').length
  const widest: TotalsRecord = {}
  records.forEach((record) =>
    Object.entries(record).forEach(([key, value]) => {
      if (!value) return
      const current = widest[key]
      if (!current || digitCount(value) > digitCount(current))
        widest[key] = value.replace(/\d/g, digit)
    })
  )
  return widest
}

/** The header cells' widths, fractions kept: the fixed-layout copies reproduce the same columns. */
const columnWidths = (root: HTMLElement): number[] => {
  const headRow = root.querySelector('[data-measure-block="items"] [data-measure="items-head"] tr')
  return headRow
    ? Array.from(headRow.children).map((cell) => cell.getBoundingClientRect().width)
    : []
}
